import React from "react";
import { Field } from "formik";

import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { styled } from "@mui/material/styles";

import { CheckListIcon } from "@components/Icons";
import CnpjCpfField from "@components/CnpjCpfField";

import { useUserForm } from "../hook/useUserForm";
import { CNPJTable } from "./CNPJTable";

export const STextField = styled(TextField)({
  width: "100%",
  "& label": {
    fontFamily: "Montserrat",
    fontWeight: 400,
    fontSize: "1.4rem",
    borderRadius: "1rem",
  },
  "& .MuiInputLabel-shrink": {
    fontWeight: 700,
    color: "#3d3d3d",
    fontSize: "1.4rem",
    backgroundColor: "white",
    paddingRight: 6,
  },
  "& .MuiOutlinedInput-notchedOutline": { borderRadius: "1rem" },
  "& .MuiInputBase-input": { fontFamily: "Montserrat", fontSize: "1.4rem" },
});

const containerSx = {
  backgroundColor: "#fff",
  p: { xs: "1.5rem", sm: "8px 32px 32px 8px" },
  m: { xs: "0.5rem", sm: "24px 0px 0px 48px" },
  borderRadius: 1,
  width: { xs: "100%", sm: "95%" },
};

const inputLabelSx = {
  position: "absolute" as const,
  color: "#3E3D3D",
  fontFamily: "Montserrat",
  fontSize: "1.4rem",
  backgroundColor: "#fff",
  pl: "0.2rem",
  pr: "0.8rem",
};

const btnTextSx = (disabled: boolean) => ({
  fontWeight: "bold",
  textTransform: "none" as const,
  fontSize: "1.4rem",
  color: disabled ? "grey.500" : "primary.main",
});

interface UserFormProps {
  loading: boolean;
  title: string;
  submitLabel: string;
  submitLoadingLabel: string;
  onCancel: () => void;
}

const UserForm: React.FC<UserFormProps> = ({
  loading,
  title,
  submitLabel,
  submitLoadingLabel,
  onCancel,
}) => {
  const {
    values,
    errors,
    touched,
    setFieldValue,
    handleChange,
    handleBlur,
    handleProfileChange,
    handleAddCNPJ,
    handleSearchBPID,
    handleRemoveCNPJ,
    profileOptions,
    isCarrierProfile,
    isDriverBPIDValid,
    isGroupCNPJEmpty,
    isCNPJFieldsDisabled,
    isFormInvalid,
    isLoading,
  } = useUserForm();

  const bpidField = (label: string) => (
    <STextField
      name="bp"
      label={label}
      variant="outlined"
      value={values.attributes?.BPID}
      onChange={handleChange("attributes.BPID")}
      onBlur={handleBlur("attributes.BPID")}
      error={touched.attributes?.BPID && !!errors.attributes?.BPID}
      helperText={touched.attributes?.BPID && errors.attributes?.BPID}
    />
  );

  return (
    <Grid container spacing={3} sx={containerSx}>
      <Grid
        container
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        item
        xs={12}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: { xs: "1.6rem", sm: "2rem" },
            color: "#3E3D3D",
          }}
        >
          {title}
        </Typography>
        <CheckListIcon style={{ height: "2rem" }} />
      </Grid>

      <Grid item xs={12} md={6}>
        <Grid container spacing={{ xs: 2, sm: 4 }}>
          <Grid
            item
            xs={12}
            {...(isDriverBPIDValid && { md: 6 })}
            sx={{ mt: { xs: 1.5, sm: 2 } }}
          >
            <FormControl fullWidth variant="outlined">
              <InputLabel
                htmlFor="profile"
                sx={{
                  ...inputLabelSx,
                  fontWeight: touched.profile ? "bold" : "normal",
                }}
              >
                Tipo de usuário
              </InputLabel>
              <Field
                name="profile"
                label="Perfil"
                as={Select}
                fullWidth
                error={touched.profile && !!errors.profile}
                IconComponent={KeyboardArrowDownIcon}
                sx={{ borderRadius: 2 }}
                onChange={(e: React.ChangeEvent<{ value: unknown }>) =>
                  handleProfileChange(e.target.value)
                }
              >
                {profileOptions.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Field>
              {touched.profile && errors.profile && (
                <Typography variant="caption" color="error">
                  {errors.profile}
                </Typography>
              )}
            </FormControl>
          </Grid>

          {isDriverBPIDValid && (
            <Grid item xs={12} md={6} sx={{ mt: { xs: 1.5, sm: 2 } }}>
              {bpidField("BP do motorista")}
            </Grid>
          )}

          <Grid item xs={12} md={6}>
            <STextField
              name="firstName"
              label="Primeiro nome"
              variant="outlined"
              value={values.firstName}
              onChange={handleChange("firstName")}
              onBlur={handleBlur("firstName")}
              error={touched.firstName && !!errors.firstName}
              helperText={touched.firstName && errors.firstName}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <STextField
              name="lastName"
              label="Sobrenome"
              variant="outlined"
              value={values.lastName}
              onChange={handleChange("lastName")}
              onBlur={handleBlur("lastName")}
              error={touched.lastName && !!errors.lastName}
              helperText={touched.lastName && errors.lastName}
            />
          </Grid>
          <Grid item xs={12}>
            <STextField
              name="email"
              label="E-mail"
              variant="outlined"
              value={values.email}
              onChange={handleChange("email")}
              onBlur={handleBlur("email")}
              error={touched.email && !!errors.email}
              helperText={touched.email && errors.email}
            />
          </Grid>

          {isCarrierProfile && (
            <Grid
              item
              xs={12}
              sx={{
                border: "1px dashed",
                borderColor: "grey.400",
                borderRadius: 1,
              }}
            >
              <Grid item xs={12} sx={{ mt: 2 }}>
                {bpidField("BP da transportadora")}
              </Grid>
              <Box height="3rem" />
              <Grid item xs={12}>
                <STextField
                  name="razaoSocial"
                  label={isLoading ? "Pesquisando..." : "Razão Social"}
                  variant="outlined"
                  value={values.attributes.INTERNAL_NAME}
                  disabled
                />
              </Grid>
              <Grid
                item
                xs={12}
                sx={{ display: "flex", justifyContent: "end", pt: 2 }}
              >
                <Button
                  onClick={handleSearchBPID}
                  disabled={!values.attributes?.BPID || isLoading}
                >
                  <Tooltip
                    title={
                      !isGroupCNPJEmpty
                        ? "Ao pesquisar novamente, os CNPJs listados serão excluídos"
                        : ""
                    }
                  >
                    <Typography
                      sx={btnTextSx(!values.attributes?.BPID || isLoading)}
                    >
                      {isLoading ? "Pesquisando..." : "Pesquisar CNPJs"}
                    </Typography>
                  </Tooltip>
                </Button>
              </Grid>
            </Grid>
          )}
        </Grid>

        <Grid
          container
          spacing={{ xs: 2, sm: 4 }}
          sx={{ mt: { xs: 1, sm: 2 } }}
        >
          <Grid item xs={12} md={6}>
            <CnpjCpfField
              name="CNPJ"
              label="CNPJ ou CPF"
              disabled={isCNPJFieldsDisabled}
              value={values.CNPJ || ""}
              onChange={(raw) => setFieldValue("CNPJ", raw)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <STextField
              disabled={isCNPJFieldsDisabled}
              name="companyName"
              variant="outlined"
              label="Razão Social"
              value={values.companyName}
              onChange={handleChange("companyName")}
            />
          </Grid>
          <Grid
            item
            xs={12}
            sx={{ display: "flex", justifyContent: "end", pt: 0 }}
          >
            <Button onClick={handleAddCNPJ} disabled={isCNPJFieldsDisabled}>
              <Typography sx={btnTextSx(isCNPJFieldsDisabled)}>
                + Adicionar
              </Typography>
            </Button>
          </Grid>
        </Grid>
      </Grid>

      <Grid item xs={12} md={6}>
        <CNPJTable
          items={values.attributes.CNPJ}
          onRemove={handleRemoveCNPJ}
          isEmpty={isGroupCNPJEmpty}
          isLoading={isLoading}
          isCarrierProfile={isCarrierProfile}
        />
      </Grid>

      <Grid
        item
        xs={12}
        sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}
      >
        <Button onClick={onCancel} variant="text" color="primary" size="large">
          Cancelar
        </Button>
        <Button
          variant="contained"
          color="primary"
          size="large"
          type="submit"
          disabled={isFormInvalid}
          sx={{ boxShadow: "none" }}
          endIcon={loading && <CircularProgress color="secondary" size={20} />}
        >
          {loading ? submitLoadingLabel : submitLabel}
        </Button>
      </Grid>
    </Grid>
  );
};

export default UserForm;
